// ─── PRICE CALCULATION UTILITIES ─────────────────────────────────────────────
// Margin is calculated ON PVP (not on cost)
// Formula: PVP_sin_iva = coste_neto / (1 - margen)
// PVP_con_iva = PVP_sin_iva * (1 + iva / 100)

// Default psychological rounding rule
export const REGLA_REDONDEO_DEFAULT = 'terminar_99';

// Margin default (35% on PVP as per spec)
export const MARGEN_DEFAULT = 0.35;

/**
 * Calculate PVP from net cost using margin-on-PVP formula
 * @param {number} costeNeto - Net purchase price (without IVA)
 * @param {number} margen - Margin as decimal (e.g. 0.40 for 40%)
 * @param {number} ivaPct - IVA percentage (e.g. 10)
 * @returns {{ pvpSinIva, pvpConIva, margenReal }}
 */
export const calcularPvp = (costeNeto, margen, ivaPct = 10) => {
  if (!costeNeto || costeNeto <= 0) return { pvpSinIva: 0, pvpConIva: 0, margenReal: 0 };
  if (!margen || margen <= 0 || margen >= 1) return { pvpSinIva: costeNeto, pvpConIva: costeNeto * (1 + ivaPct / 100), margenReal: 0 };

  const pvpSinIva = costeNeto / (1 - margen);
  const pvpConIva = pvpSinIva * (1 + ivaPct / 100);
  const margenReal = (pvpSinIva - costeNeto) / pvpSinIva;

  return {
    pvpSinIva: Math.round(pvpSinIva * 10000) / 10000,
    pvpConIva: Math.round(pvpConIva * 10000) / 10000,
    margenReal: Math.round(margenReal * 10000) / 10000,
  };
};

/**
 * Calculate psychological PVP (always at or below theoretical PVP)
 * @param {number} precioTeorico - Theoretical PVP
 * @param {string} regla - Rounding rule
 * @returns {number}
 */
export const calcularPvpPsicologico = (precioTeorico, regla = 'terminar_99') => {
  if (!precioTeorico || precioTeorico <= 0) return 0;

  const precio = precioTeorico;

  switch (regla) {
    case 'TERMINAR_99': case'terminar_99': {
      const base = Math.floor(precio);
      const candidate = base - 0.01;
      return candidate > 0 && candidate <= precio ? candidate : precio;
    }
    case 'TERMINAR_95': case'terminar_95': {
      const base = Math.floor(precio);
      const candidate = base - 0.05;
      return candidate > 0 && candidate <= precio ? candidate : precio;
    }
    case 'TERMINAR_90': case'terminar_90': {
      const base = Math.floor(precio);
      const candidate = base - 0.10;
      return candidate > 0 && candidate <= precio ? candidate : precio;
    }
    case 'multiplos_005': {
      return Math.floor(precio / 0.05) * 0.05;
    }
    case 'multiplos_010': {
      return Math.floor(precio / 0.10) * 0.10;
    }
    case 'sin_redondeo': {
      return Math.round(precio * 100) / 100;
    }
    case 'a_la_baja': {
      return Math.floor(precio * 100) / 100;
    }
    default:
      return Math.round(precio * 100) / 100;
  }
};

/**
 * Calculate net purchase price from invoice line
 * @param {number} precioUnitario - Gross unit price
 * @param {number} descuentoImporte - Discount amount per unit
 * @param {number} descuentoPorcentaje - Discount percentage
 * @returns {number} Net price (precio_compra_neto)
 */
export const calcularPrecioCompraNeto = (precioUnitario, descuentoImporte = 0, descuentoPorcentaje = 0) => {
  if (!precioUnitario || precioUnitario <= 0) return 0;
  // Percentage discount takes priority over amount discount
  if (descuentoPorcentaje > 0) {
    return precioUnitario * (1 - descuentoPorcentaje / 100);
  }
  if (descuentoImporte > 0) {
    return Math.max(0, precioUnitario - descuentoImporte);
  }
  return precioUnitario;
};

/**
 * Calculate full cost breakdown for a purchase line
 *
 * Formulas (per ERP spec):
 *   precio_compra_neto      = precio_bruto × (1 - descuento%)
 *   precio_compra_con_iva   = precio_compra_neto × (1 + iva/100)
 *   coste_con_impuestos     = precio_compra_neto
 *                             + precio_compra_neto × iva/100
 *                             + precio_compra_neto × recargo/100
 *                           = precio_compra_neto × (1 + iva/100 + recargo/100)
 *
 * IVA and recargo are BOTH calculated on precio_compra_neto (base neta).
 * recargo is NOT applied on top of the already-IVA-included price.
 *
 * @param {object} params
 * @returns {{ costeNeto, costeConIva, costeConImpuestos, porteProrrateo }}
 */
export const calcularCosteLinea = ({
  precioUnitario = 0,
  descuentoImporte = 0,
  descuentoPorcentaje = 0,
  ivaPct = 10,
  recargoEquivalenciaPct = 0,
  porteProrrateo = 0,
}) => {
  // precio_compra_neto: after discounts, before taxes, plus porte proration
  const costeNeto = calcularPrecioCompraNeto(precioUnitario, descuentoImporte, descuentoPorcentaje) + porteProrrateo;

  // precio_compra_con_iva: neto + IVA
  const costeConIva = costeNeto * (1 + ivaPct / 100);

  // coste_con_impuestos: neto + IVA + recargo (both on base neta)
  // NOT: costeConIva * (1 + recargo/100) — that would compound incorrectly
  const costeConImpuestos = costeNeto * (1 + ivaPct / 100 + recargoEquivalenciaPct / 100);

  return {
    costeNeto: Math.round(costeNeto * 10000) / 10000,
    costeConIva: Math.round(costeConIva * 10000) / 10000,
    costeConImpuestos: Math.round(costeConImpuestos * 10000) / 10000,
    porteProrrateo: Math.round(porteProrrateo * 10000) / 10000,
  };
};

/**
 * Get applicable margin for a product
 * Priority: product margin → category margin → family margin → default (35%)
 * @param {object} producto - Product object with optional categoria and familia
 * @param {number} defaultMargen - Default margin (0-1)
 * @returns {number} Margin as decimal
 */
export const getMargenAplicable = (producto, defaultMargen = MARGEN_DEFAULT) => {
  // 1. Product-level margin
  if (producto?.margen_objetivo != null && producto?.margen_objetivo > 0) {
    return producto?.margen_objetivo / 100;
  }
  // 2. Category margin
  if (producto?.categoria?.margen_objetivo != null && producto?.categoria?.margen_objetivo > 0) {
    return producto?.categoria?.margen_objetivo / 100;
  }
  // 3. Family margin (via categoria.familia_id or producto.familia_id)
  if (producto?.familia?.margen_objetivo != null && producto?.familia?.margen_objetivo > 0) {
    return producto?.familia?.margen_objetivo / 100;
  }
  // 4. Default
  return defaultMargen;
};

/**
 * Get applicable rounding rule for a product
 * Priority: product → category → family → default
 */
export const getReglaRedondeo = (producto, defaultRegla = REGLA_REDONDEO_DEFAULT) => {
  if (producto?.regla_redondeo) return producto?.regla_redondeo;
  if (producto?.categoria?.regla_redondeo) return producto?.categoria?.regla_redondeo;
  if (producto?.familia?.regla_redondeo) return producto?.familia?.regla_redondeo;
  return defaultRegla;
};

/**
 * Calculate PVP suggestion for a product given its cost
 * Respects bloqueo_precio and actualizar_pvp_automatico flags
 * @returns {{ pvpSugerido, pvpTeorico, pvpActual, margenReal, margenAplicado, bloqueado, regla }}
 */
export const calcularPvpSugerido = (producto, costeNeto, ivaPct = 10) => {
  const margen = getMargenAplicable(producto);
  const regla = getReglaRedondeo(producto);
  const { pvpSinIva, pvpConIva, margenReal } = calcularPvp(costeNeto, margen, ivaPct);
  const pvpPsico = calcularPvpPsicologico(pvpConIva, regla);
  const pvpActual = producto?.pvp_venta || 0;
  const bloqueado = producto?.bloqueo_precio === true;
  const autoActualizar = producto?.actualizar_pvp_automatico !== false;

  return {
    pvpSugerido: pvpPsico,
    pvpTeorico: pvpConIva,
    pvpSinIva,
    pvpActual,
    margenReal,
    margenAplicado: margen,
    bloqueado,
    autoActualizar,
    regla,
    debeActualizar: !bloqueado && autoActualizar,
  };
};

/**
 * Format price for display
 */
export const fmtPrecio = (n, d = 2) =>
  typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';

export const fmtEurPrecio = (n) =>
  typeof n === 'number' ? `${fmtPrecio(n)} €` : '—';

export const fmtPct = (n) =>
  typeof n === 'number' ? `${(n * 100)?.toFixed(1)} %` : '—';
